import React from 'react';
import { cn } from '@/lib/utils';

interface HeatmapZone {
  zoneId: string;
  label: string;
  averageSeconds: number;
  percentOfTotal: number;
  intensity: 'high' | 'medium' | 'low';
}

interface ArticleHeatmapOverlayProps {
  zones: HeatmapZone[];
  articleTitle: string;
  intervieweeName: string;
  className?: string;
}

/**
 * Visual heatmap overlay showing where readers spent time.
 * Used in reports to visualize attention distribution.
 */
export function ArticleHeatmapOverlay({
  zones,
  articleTitle,
  intervieweeName,
  className,
}: ArticleHeatmapOverlayProps) {
  // Map zone IDs to positions in the article layout
  const zonePositions: Record<string, { top: string; height: string; label: string }> = {
    'expert-intro': { top: '0%', height: '15%', label: 'Ekspert-intro' },
    'context': { top: '15%', height: '10%', label: 'Kontekst' },
    'question': { top: '25%', height: '10%', label: 'Spørsmål' },
    'answer': { top: '35%', height: '25%', label: 'Svar' },
    'tags': { top: '60%', height: '8%', label: 'Hashtags' },
    'summary': { top: '68%', height: '10%', label: 'AI-oppsummering' },
    'kai': { top: '78%', height: '8%', label: 'Kai Chat' },
    'quiz': { top: '86%', height: '7%', label: 'Quiz' },
    'debate': { top: '93%', height: '7%', label: 'Debatt' },
  };

  const intensityColors = {
    high: 'bg-green-500/40 border-green-500',
    medium: 'bg-yellow-500/40 border-yellow-500',
    low: 'bg-red-500/40 border-red-500',
  };

  return (
    <div className={cn('relative bg-muted rounded-lg overflow-hidden', className)}>
      {/* Article skeleton */}
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-muted-foreground/20" />
          <div className="flex-1">
            <div className="h-4 w-32 bg-muted-foreground/20 rounded mb-1" />
            <div className="h-3 w-24 bg-muted-foreground/10 rounded" />
          </div>
        </div>
        <div className="h-5 w-full bg-muted-foreground/20 rounded" />
        <div className="h-4 w-3/4 bg-muted-foreground/15 rounded" />
        <div className="space-y-2 mt-4">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="h-3 bg-muted-foreground/10 rounded"
              style={{ width: `${75 + Math.random() * 25}%` }}
            />
          ))}
        </div>
        <div className="flex gap-2 mt-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-6 w-16 bg-muted-foreground/15 rounded-full" />
          ))}
        </div>
      </div>

      {/* Heatmap overlay */}
      <div className="absolute inset-0">
        {zones.map((zone) => {
          const position = zonePositions[zone.zoneId];
          if (!position) return null;

          return (
            <div
              key={zone.zoneId}
              className={cn(
                'absolute left-0 right-0 border-l-4 transition-all',
                intensityColors[zone.intensity]
              )}
              style={{
                top: position.top,
                height: position.height,
              }}
            >
              <div className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
                <span className="text-xs font-medium text-foreground bg-background/80 px-2 py-1 rounded">
                  {position.label}
                </span>
                <span className="text-xs text-muted-foreground bg-background/80 px-2 py-1 rounded">
                  {Math.round(zone.averageSeconds)}s ({zone.percentOfTotal.toFixed(0)}%)
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="absolute bottom-2 right-2 flex items-center gap-3 bg-background/90 px-3 py-2 rounded-lg shadow">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-green-500" />
          <span className="text-xs">Høy</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-yellow-500" />
          <span className="text-xs">Medium</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <span className="text-xs">Lav</span>
        </div>
      </div>
    </div>
  );
}

export default ArticleHeatmapOverlay;
